import { Component, OnInit, OnDestroy } from '@angular/core';
import { LoadingController, AlertController, ModalController } from '@ionic/angular'
import { AuthenticationService } from 'src/app/core/services/authentication.service'
import { TranslatePipe } from '@ngx-translate/core';
import { NavService } from 'src/app/core/services/nav.service';
import { RegisterPage } from '../register/register.page';
import { ForgotPasswordPage } from '../forgot-password/forgot-password.page';
import { KeenTrackingService } from 'src/app/core/services/keenTracking.service';
import { SocialAuth } from '../../core/services/SocialAuth.service'
import { ModalService } from 'src/app/core/services/modal.service';
import { ModalPage } from '../modal/modal.page';
import { RestaurantService } from 'src/app/core';
import { environment } from 'src/environments/environment';

declare const grecaptcha: {
	render: (elementId: string, options: { sitekey: string }) => number;
	getResponse: (widgetId: number) => string;
	reset: (widgetId: number) => void;
};

@Component({
	selector: 'app-login',
	templateUrl: './login.page.html',
	styleUrls: ['./login.page.scss'],
})
export class LoginPage implements OnInit, OnDestroy {

	email: string = ''
	password: string = ''
	restaurantUrl: string
	next: string = ''
	showPassword: boolean = false
	public isNewHomePedirDelivery: boolean;

	private recaptchaV2WidgetId: number | null = null;
	private recaptchaCheckInterval: ReturnType<typeof setInterval> | null = null;

	/**
	 * Bypass de reCAPTCHA APENAS em teste automatizado. Guarda dura: NUNCA em produção
	 * (`environment.production`) e só quando a app roda sob Cypress (`window.Cypress`).
	 * Dev/QA manual seguem exigindo o captcha normalmente. Ver TEST-METRICS.md (#4).
	 */
	private get captchaDisabled(): boolean {
		return !environment.production && typeof window !== 'undefined' && !!(window as any).Cypress;
	}

	constructor(
		private authService: AuthenticationService,
		private socialAuthService: SocialAuth,
		private loadingController: LoadingController,
		private alertController: AlertController,
		private translatePipe: TranslatePipe,
		private navService: NavService,
		private modalController: ModalController,
		private keen: KeenTrackingService,
		private modalService: ModalService,
		private restaurantService: RestaurantService
	) { }

	async ngOnInit() {
		this.isNewHomePedirDelivery = !!this.restaurantService.restaurant?.info?.modules?.new_home_pedir_delivery;
		this.initRecaptchaV2();
	}

	ngOnDestroy() {
		if (this.recaptchaCheckInterval) {
			clearInterval(this.recaptchaCheckInterval);
		}
	}

	/**
	 * Recaptcha saiu do index.html (Caso 16): pesava o boot de TODAS as
	 * visitas e só o login o consome. Injeta os scripts on-demand; o poller
	 * de 100ms do initRecaptchaV2 já espera o grecaptcha ficar pronto.
	 */
	private ensureRecaptchaScripts() {
		if (document.getElementById('mp-recaptcha-loader')) return;
		const v2 = document.createElement('script');
		v2.id = 'mp-recaptcha-loader';
		v2.src = 'https://www.google.com/recaptcha/api.js?hl=pt-BR';
		v2.async = true;
		document.head.appendChild(v2);
		const enterprise = document.createElement('script');
		enterprise.src = 'https://www.google.com/recaptcha/enterprise.js?render=6LfBJNUpAAAAADi0E6LL1RHoz2ACPGrlpoy9jD8e';
		enterprise.async = true;
		document.head.appendChild(enterprise);
	}

	private initRecaptchaV2() {
		if (this.captchaDisabled) return; // teste: não renderiza o widget
		this.ensureRecaptchaScripts();
		this.recaptchaCheckInterval = setInterval(() => {
			if (typeof grecaptcha !== 'undefined' && grecaptcha.render && document.getElementById('recaptcha-v2')) {
				clearInterval(this.recaptchaCheckInterval!);
				this.recaptchaV2WidgetId = grecaptcha.render('recaptcha-v2', {
					sitekey: '6Lfm_IYmAAAAACPNpNM7Qx2op-knVM5AjpP_5DTk',
				});
			}
		}, 100);
	}

	async goToRegister() {
		const nav = document.querySelector('ion-nav');
        if (nav) {
            return nav.push(RegisterPage);
        }
		this.navService.push('register');
		this.modalController.dismiss();
	}

	async goToForgotPassword() {
		if (this.isNewHomePedirDelivery) {
			this.modalService.open({
				component: ForgotPasswordPage,
				cssClass: 'modal-sm',
				props: {
					backToLoginFn: () => this.modalService.open({ component: ModalPage, props: { root: LoginPage } })
				}
			});
			return;
		}
		const nav = document.querySelector('ion-nav');
		if (nav) {
			return nav.push(ForgotPasswordPage);
		}
		const modal = await this.modalController.getTop();
		if (modal) await this.modalController.dismiss();
		this.navService.push('forgot-password');
	}

	loginByEmail() {
		if (this.email == '') {
			this.pageLoginAlert(this.translatePipe.transform("Insert an email!"))
			return;
		}

		if (this.password == '') {
			this.pageLoginAlert(this.translatePipe.transform("Insert a password!"))
			return;
		}

		const captchaToken = this.captchaDisabled
			? 'e2e-bypass'
			: (this.recaptchaV2WidgetId !== null ? grecaptcha.getResponse(this.recaptchaV2WidgetId) : '');
		if (!this.captchaDisabled && !captchaToken) {
			this.pageLoginAlert(this.translatePipe.transform("Please complete the reCAPTCHA."))
			return;
		}
		this.loadingLogin('email', captchaToken);
	}

	loginWithGoogle() {
		this.socialAuthService.login().then(() => {
			this.goDismiss({ loginSuccess: true });
		});
	}

	async loadingLogin(mode: string, captchaToken?: string) {
		const loading = await this.loadingController.create({
			message: `${this.translatePipe.transform('Wait')}...`
		})
		await loading.present()

		if (mode == 'email') {
			const loginData: { email: string; password: string; loginMode: string; captchaToken?: string } = {
				email: this.email,
				password: this.password,
				loginMode: 'email',
				...(captchaToken && { captchaToken }),
			};

			this.authService.login(loginData, 'email').then(() => {
				loading.dismiss()
				this.email = '';
				this.password = '';
				this.goDismiss({ loginSuccess: true });

				this.keen.setEventBase().then(() => {
					this.keen.event('login_email');
				})
			}, error => {
				loading.dismiss()
				if (this.recaptchaV2WidgetId !== null) {
					grecaptcha.reset(this.recaptchaV2WidgetId);
				}
				if (error.error == 'Email does not exist.') {
					this.pageLoginAlert(this.translatePipe.transform("Email does not exist!"))
				} else if (error.error == 'Email or password is wrong.') {
					this.pageLoginAlert(this.translatePipe.transform("Email or password is wrong!"))
				}
			})
		}
	}

	async pageLoginAlert(message: string) {
		const alert = await this.alertController.create({
			cssClass: 'my-custom-class',
			header: message,
			buttons: ['OK']
		});

		await alert.present();
	}

	async goDismiss(event: any = {}) {
		this.modalService.close();

		const modal = await this.modalController.getTop();
		if (modal) {
		  return this.modalController.dismiss(event);
		}

		this.navService.push('menu');
	}

	async goBack() {
		this.goDismiss();
	}
}
